import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { colors } from "../../style";
import {
  formatSeconds,
  speakerColours,
  turnAt,
} from "../../utils/meetingFormat";
import {
  Btn,
  Card,
  Checkbox,
  Chip,
  Dialog,
  errorMessage,
  Icon,
  LinkButton,
  PillButton,
  SectionLabel,
  Spinner,
  TextInput,
  ui,
} from "../ui";

// index is the turn picked, or -1 when renaming the speaker as a whole
export function renameTarget(conversation, name, index = -1) {
  return {
    name,
    index,
    occurrences: conversation.filter((b) => b.participant === name).length,
  };
}

// The picked turn, else what the speaker said first, so they can be named
// without reading on
function sample(conversation, target) {
  const turn = conversation[target.index];
  // The index can go stale if the meeting refetches while the dialog is open
  const picked =
    turn?.participant === target.name ? turn.content?.trim() : undefined;
  const said = picked
    ? [picked]
    : conversation
        .filter((b) => b.participant === target.name)
        .map((b) => (b.content ?? "").trim())
        .filter(Boolean)
        .slice(0, 2);
  return said.map((t) =>
    t.length > 140 ? `${t.slice(0, 140).trimEnd()}…` : t,
  );
}

// "Speaker: text", a line per turn
export const transcriptText = (conversation) =>
  conversation
    .map((b) => `${b.participant || "Speaker"}: ${b.content ?? ""}`)
    .join("\n");

const style = {
  turn: (highlight) => ({
    display: "flex",
    gap: "14px",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "14px",
    lineHeight: 1.6,
    flexWrap: "wrap",
    ...(highlight
      ? {
          background: "rgba(224, 179, 77, 0.08)",
          boxShadow: `inset 0 0 0 1px ${colors.amberBorder}`,
        }
      : {}),
  }),
  time: (bookmarked) => ({
    flex: "0 0 52px",
    fontSize: "12px",
    paddingTop: "2px",
    color: bookmarked ? colors.amberText : colors.textLight,
    fontVariantNumeric: "tabular-nums",
  }),
  who: (colour) => ({
    flex: "0 0 130px",
    minWidth: 0,
    border: 0,
    background: "none",
    padding: 0,
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 700,
    color: colour,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

// rename: a mutation taking { original, name, change } (useRenameParticipant
// for meetings). Untimed transcripts (calls) pass showTimes={false}.
export function TranscriptTab({
  meeting,
  loading,
  focusAt,
  rename,
  subject = "meeting",
  emptyText = "The transcript isn't ready yet.",
  showTimes = true,
}) {
  const conversation = useMemo(
    () => meeting.transcription?.conversation ?? [],
    [meeting.transcription],
  );
  const { colours, counts } = useMemo(() => {
    const counts = new Map();
    for (const b of conversation) {
      counts.set(b.participant, (counts.get(b.participant) ?? 0) + 1);
    }
    return { colours: speakerColours(conversation), counts };
  }, [conversation]);

  // Turn index -> the bookmarks that fall in it
  const bookmarked = useMemo(() => {
    const map = new Map();
    for (const b of meeting.bookmarks ?? []) {
      const i = turnAt(conversation, b.at);
      if (i >= 0) map.set(i, [...(map.get(i) ?? []), b]);
    }
    return map;
  }, [conversation, meeting.bookmarks]);

  const focused = focusAt ? turnAt(conversation, focusAt.at) : -1;
  const focusedRef = useRef(null);
  useEffect(() => {
    focusedRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focused, focusAt]);

  const [target, setTarget] = useState(null);

  if (!conversation.length) {
    return (
      <Card>
        {loading ? (
          <Spinner />
        ) : (
          <p style={{ ...ui.muted, margin: 0 }}>{emptyText}</p>
        )}
      </Card>
    );
  }

  // navigator.clipboard is missing outside secure contexts
  const copy = () =>
    (
      navigator.clipboard?.writeText(transcriptText(conversation)) ??
      Promise.reject()
    ).then(
      () => toast("Transcript copied", { type: "success" }),
      () => toast("Couldn't copy the transcript", { type: "error" }),
    );

  const openRename = (name, index) =>
    setTarget(renameTarget(conversation, name, index));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          alignItems: "center",
          fontSize: "13px",
        }}
      >
        <span style={ui.muted}>Speakers:</span>
        {[...counts.keys()].map((name) => {
          const colour = colours.get(name);
          return (
            <Chip
              key={name}
              as="button"
              color={colour}
              title="Rename this speaker"
              onClick={() => openRename(name)}
              style={{
                gap: "5px",
                // Unnamed speakers get a dashed outline
                ...(/^speaker\b/i.test(name) || !name
                  ? { border: `1px dashed ${colour}99` }
                  : null),
              }}
            >
              {name || "Speaker"}
              <Icon name="pencil" size={11} />
            </Chip>
          );
        })}
        {!loading && (
          <LinkButton style={{ marginLeft: "auto" }} onClick={copy}>
            <Icon name="copy" size={12} />
            Copy transcript
          </LinkButton>
        )}
      </div>

      <Card style={{ padding: "8px" }}>
        {conversation.map((b, i) => {
          const marks = bookmarked.get(i);
          const colour = colours.get(b.participant) ?? colors.teal;
          return (
            <div
              key={i}
              ref={i === focused ? focusedRef : undefined}
              style={style.turn(!!marks || i === focused)}
            >
              {showTimes && (
                <span style={style.time(!!marks)}>
                  {marks ? "★ " : ""}
                  {b.start != null ? formatSeconds(b.start) : ""}
                </span>
              )}
              <button
                type="button"
                title="Rename this speaker"
                style={style.who(colour)}
                onClick={() => openRename(b.participant, i)}
              >
                {b.participant || "Speaker"}
              </button>
              <span style={{ flex: "1 1 260px", minWidth: 0 }}>
                {marks?.some((m) => m.label) && (
                  <span
                    style={{
                      display: "block",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: colors.amberText,
                    }}
                  >
                    {marks
                      .map((m) => m.label)
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
                {b.content}
              </span>
            </div>
          );
        })}
      </Card>

      <RenameSpeakerDialog
        meeting={meeting}
        rename={rename}
        subject={subject}
        target={target}
        onClose={() => setTarget(null)}
      />
    </div>
  );
}

export function RenameSpeakerDialog({
  meeting,
  rename,
  subject,
  target,
  onClose,
}) {
  const [name, setName] = useState("");
  const [everywhere, setEverywhere] = useState(true);
  const { reset } = rename;

  useEffect(() => {
    if (target) {
      setName(
        !target.name || /^speaker\b/i.test(target.name) ? "" : target.name,
      );
      setEverywhere(true);
      reset();
    }
  }, [target, reset]);

  // People from the calendar invite not already used as a speaker
  const suggestions = useMemo(() => {
    const used = new Set(
      (meeting.transcription?.conversation ?? []).map((b) =>
        (b.participant ?? "").toLowerCase(),
      ),
    );
    return (meeting.attendees ?? [])
      .map((a) => (a.name ?? "").trim())
      .filter(
        (n, i, all) => n && !used.has(n.toLowerCase()) && all.indexOf(n) === i,
      );
  }, [meeting]);

  const said = useMemo(
    () =>
      target ? sample(meeting.transcription?.conversation ?? [], target) : [],
    [meeting.transcription, target],
  );

  const save = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || rename.isLoading) return;
    rename.mutate(
      {
        original: target.name,
        name: trimmed,
        change: everywhere ? "all" : target.index,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      label="Rename speaker"
      width={380}
    >
      <form
        onSubmit={save}
        style={{
          padding: "18px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          fontSize: "13px",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SectionLabel as="span">
            Rename {target?.name || "speaker"}
          </SectionLabel>
          <TextInput
            value={name}
            autoFocus
            maxLength={100}
            placeholder="e.g. Priya Shah"
            onChange={(e) => setName(e.target.value)}
            style={{ borderColor: colors.teal }}
          />
        </label>
        {target && (
          <div
            style={{
              ...ui.muted,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "12px",
            }}
          >
            {said.map((t, i) => (
              <p key={i} style={{ margin: 0 }}>
                “{t}”
              </p>
            ))}
          </div>
        )}
        {suggestions.length > 0 && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {suggestions.map((n) => (
              <PillButton
                key={n}
                size="sm"
                selected={n === name.trim()}
                onClick={() => setName(n)}
              >
                {n}
              </PillButton>
            ))}
          </div>
        )}
        {target?.occurrences > 1 && target.index >= 0 && (
          <Checkbox checked={everywhere} onChange={setEverywhere}>
            Everywhere in this {subject} ({target.occurrences} times)
          </Checkbox>
        )}
        {rename.error && <p style={ui.error}>{errorMessage(rename.error)}</p>}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            as="button"
            type="submit"
            variant="primary"
            disabled={!name.trim()}
            loading={rename.isLoading}
          >
            Rename
          </Btn>
        </div>
      </form>
    </Dialog>
  );
}
